import React, { useId, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { TimelineOutlined, TimelineRounded } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { colors } from "../../theme/colors";
import { TooltipShell } from "../common/Tooltip";
import { EmptyDataState } from "../common/EmptyDataState";

const MotionBox = motion(Box);
const COMMUNITY_AVG_COLOR = "#34D399";

const RANGE_OPTIONS = [
    { id: "30", label: "30 days", days: 30 },
    { id: "90", label: "90 days", days: 90 },
    { id: "all", label: "all time", days: null },
];

const MIN_X_TICK_COUNT = 10;
const MAX_X_TICK_COUNT = 15;
const MOBILE_HISTORY_DAYS = 15;
const MOBILE_X_TICK_COUNT = 5;
const MOBILE_LINE_POINTS = 15;
const MIN_LINE_POINTS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const CHART_NO_FOCUS_SX = {
    outline: "none",
    userSelect: "none",
    WebkitTapHighlightColor: "transparent",
    "&:focus": { outline: "none" },
    "&:focus-visible": { outline: "none" },
    "& .recharts-wrapper": { outline: "none !important" },
    "& .recharts-surface": { outline: "none !important" },
    "& svg": { outline: "none" },
    "& svg:focus": { outline: "none" },
    "& *:focus": { outline: "none" },
    "& *:focus-visible": { outline: "none" },
};

function ReputationRangeFilterButton({ label, isActive, onClick, layoutId = "activeReputationRangeFilter" }) {
    return (
        <MotionBox
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={isActive}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            sx={{
                position: "relative",
                overflow: "hidden",
                border: "none",
                cursor: "pointer",
                borderRadius: "999px",
                px: "1rem",
                py: "0.58rem",
                minWidth: "84px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.74rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: isActive ? colors.text.primary : "rgba(255,255,255,0.46)",
                background: isActive
                    ? `linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(246,185,76,0.10) 100%)`
                    : "rgba(255,255,255,0.015)",
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: isActive ? "rgba(246,185,76,0.24)" : "rgba(255,255,255,0.06)",
                backdropFilter: "blur(10px)",
                boxShadow: isActive
                    ? `0 0 0 1px rgba(246,185,76,0.04), 0 6px 18px rgba(0,0,0,0.24), 0 0 24px rgba(246,185,76,0.08)`
                    : `inset 0 1px 0 rgba(255,255,255,0.02)`,
            }}
        >
            <AnimatePresence mode="wait">
                {isActive ? (
                    <MotionBox
                        layoutId={layoutId}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "999px",
                            border: "1px solid rgba(246,185,76,0.14)",
                            boxShadow: "0 0 22px rgba(246,185,76,0.10)",
                            pointerEvents: "none",
                        }}
                    />
                ) : null}
            </AnimatePresence>
            <Box component="span" sx={{ position: "relative", zIndex: 2 }}>
                {label}
            </Box>
        </MotionBox>
    );
}

ReputationRangeFilterButton.propTypes = {
    label: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    layoutId: PropTypes.string,
};

function parseHistoryEvents(history) {
    return (history || [])
        .map((entry) => {
            const timestamp = new Date(entry.timestamp).getTime();
            const score = Number(entry.newOverallScore ?? entry.oldOverallScore);
            const previousScore = Number(entry.oldOverallScore);

            if (!Number.isFinite(timestamp) || !Number.isFinite(score)) {
                return null;
            }

            return { timestamp, score, previousScore };
        })
        .filter(Boolean)
        .sort((left, right) => left.timestamp - right.timestamp);
}

function getRangeBounds(rangeDays, history, now = Date.now()) {
    const events = parseHistoryEvents(history);

    if (rangeDays != null) {
        return {
            startTs: now - rangeDays * DAY_MS,
            endTs: now,
        };
    }

    if (events.length > 0) {
        return {
            startTs: events[0].timestamp,
            endTs: now,
        };
    }

    return {
        startTs: now - 90 * DAY_MS,
        endTs: now,
    };
}

function getXTickCount(startTs, endTs) {
    const spanDays = Math.max(1, Math.ceil((endTs - startTs) / DAY_MS));
    const targetTicks = Math.max(MIN_X_TICK_COUNT, Math.ceil(spanDays / 3));
    return Math.min(MAX_X_TICK_COUNT, spanDays + 1, targetTicks);
}

function buildXTicks(startTs, endTs, tickCount = getXTickCount(startTs, endTs)) {
    if (tickCount < 2 || endTs <= startTs) {
        return [startTs, endTs];
    }

    return Array.from({ length: tickCount }, (_, index) => (
        startTs + (index / (tickCount - 1)) * (endTs - startTs)
    ));
}

function getPointCount(rangeDays, startTs, endTs, { isMobile = false } = {}) {
    if (isMobile) {
        return MOBILE_LINE_POINTS;
    }

    if (rangeDays != null) {
        return Math.max(MIN_LINE_POINTS, rangeDays);
    }

    const spanDays = Math.max(1, Math.ceil((endTs - startTs) / DAY_MS));
    return Math.max(MIN_LINE_POINTS, spanDays);
}

function getScoreAtTimestamp(events, timestamp, fallbackScore) {
    if (events.length > 0 && timestamp < events[0].timestamp) {
        if (Number.isFinite(events[0].previousScore)) {
            return events[0].previousScore;
        }
        return fallbackScore;
    }

    let score = fallbackScore;
    for (const event of events) {
        if (event.timestamp <= timestamp) {
            score = event.score;
        } else {
            break;
        }
    }

    return score;
}

function buildRangeSeriesPoints(startTs, endTs, pointCount, getScoreAt, communityAvg) {
    if (pointCount < 2 || endTs <= startTs) {
        return [];
    }

    return Array.from({ length: pointCount }, (_, index) => {
        const timestamp = startTs + (index / (pointCount - 1)) * (endTs - startTs);
        return {
            timestamp,
            label: format(new Date(timestamp), "MMM d"),
            score: getScoreAt(timestamp),
            communityAvg,
        };
    });
}

function buildHistoryPoints(history, currentScore, rangeDays, communityAvg, { isMobile = false } = {}) {
    const now = Date.now();
    const avg = Number.isFinite(communityAvg) ? communityAvg : null;
    const { startTs, endTs } = getRangeBounds(rangeDays, history, now);
    const events = parseHistoryEvents(history);
    const pointCount = getPointCount(rangeDays, startTs, endTs, { isMobile });
    const fallbackScore = Number.isFinite(currentScore)
        ? currentScore
        : events[events.length - 1]?.score;

    if (!Number.isFinite(fallbackScore)) {
        return [];
    }

    const getScoreAt = (timestamp) => {
        if (timestamp >= endTs && Number.isFinite(currentScore)) {
            return currentScore;
        }

        return getScoreAtTimestamp(events, timestamp, fallbackScore);
    };

    return buildRangeSeriesPoints(startTs, endTs, pointCount, getScoreAt, avg);
}

function HistoryTooltip({ active, payload }) {
    if (!active || !payload?.length) {
        return null;
    }

    const point = payload[0]?.payload;
    if (!point) {
        return null;
    }

    return (
        <TooltipShell sx={{ minWidth: "9rem" }}>
            <Typography
                sx={{
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: colors.text.muted,
                    lineHeight: 1,
                }}
            >
                {point.label}
            </Typography>
            <Typography
                sx={{
                    mt: "0.45rem",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: colors.primary.main,
                    lineHeight: 1.3,
                }}
            >
                You: {point.score}
            </Typography>
            <Typography
                sx={{
                    mt: "0.25rem",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: COMMUNITY_AVG_COLOR,
                    lineHeight: 1.3,
                }}
            >
                Community avg: {point.communityAvg ?? "—"}
            </Typography>
        </TooltipShell>
    );
}

function ChartYAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={3}
                textAnchor="end"
                fill="rgba(255,255,255,0.38)"
                fontSize={11}
                fontWeight={600}
            >
                {payload.value}
            </text>
        </g>
    );
}

function ChartXAxisTick({ x, y, payload }) {
    const label = Number.isFinite(payload?.value)
        ? format(new Date(payload.value), "MMM d")
        : payload?.value;

    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={12}
                textAnchor="middle"
                fill="rgba(255,255,255,0.38)"
                fontSize={11}
                fontWeight={600}
            >
                {label}
            </text>
        </g>
    );
}

export default function ReputationHistoryChart({
    history = [],
    currentScore = null,
    communityAvg = null,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedRangeId, setSelectedRangeId] = useState("30");
    const gradId = useId().replace(/:/g, "");
    const selectedRange = RANGE_OPTIONS.find((option) => option.id === selectedRangeId) ?? RANGE_OPTIONS[0];
    const effectiveRangeDays = isMobile ? MOBILE_HISTORY_DAYS : selectedRange.days;

    const chartData = useMemo(() => {
        const now = Date.now();
        const { startTs, endTs } = getRangeBounds(effectiveRangeDays, history, now);
        const points = buildHistoryPoints(
            history,
            currentScore,
            effectiveRangeDays,
            communityAvg,
            { isMobile },
        );
        const tickCount = isMobile
            ? MOBILE_X_TICK_COUNT
            : getXTickCount(startTs, endTs);

        return {
            points,
            rangeStart: startTs,
            rangeEnd: endTs,
            xTicks: buildXTicks(startTs, endTs, tickCount),
        };
    }, [history, currentScore, effectiveRangeDays, communityAvg, isMobile]);

    const { points, rangeStart, rangeEnd, xTicks } = chartData;

    const yDomain = useMemo(() => {
        const values = points.flatMap((point) => [point.score, point.communityAvg].filter(Number.isFinite));
        if (!values.length) {
            return [40, 100];
        }

        const min = Math.min(...values);
        const max = Math.max(...values);
        const lower = Math.max(0, Math.floor(min / 10) * 10 - 10);
        const upper = Math.min(100, Math.ceil(max / 10) * 10 + 10);
        return [Math.max(0, lower), Math.min(100, upper || 100)];
    }, [points]);

    if (currentScore == null) {
        return (
            <EmptyDataState
                title="No score history"
                icon={TimelineRounded}
                minHeight="14rem"
                embedded
            >
                Score history unlocks as reputation changes over time
            </EmptyDataState>
        );
    }

    if (points.length === 0) {
        return (
            <EmptyDataState
                title="No changes in this period"
                icon={TimelineRounded}
                minHeight="14rem"
                embedded
            >
                Try another range or check back after new reputation updates
            </EmptyDataState>
        );
    }

    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                width: "100%",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
                <TimelineOutlined sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.42)" }} />
                <Typography
                    sx={{
                        fontSize: "0.72rem",
                        fontWeight: 800,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: colors.text.primary,
                        lineHeight: 1,
                    }}
                >
                    Score history
                </Typography>
            </Box>

            <Box
                sx={{
                    display: { xs: "none", md: "flex" },
                    flexWrap: "wrap",
                    gap: "0.5rem",
                }}
            >
                {RANGE_OPTIONS.map((option) => (
                    <ReputationRangeFilterButton
                        key={option.id}
                        label={option.label}
                        isActive={selectedRangeId === option.id}
                        onClick={() => setSelectedRangeId(option.id)}
                    />
                ))}
            </Box>

            <Box
                sx={{
                    width: "100%",
                    minWidth: 0,
                    height: { xs: "14rem", md: "16rem" },
                    minHeight: { xs: "14rem", md: "16rem" },
                    ...CHART_NO_FOCUS_SX,
                }}
            >
                <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                    <AreaChart data={points} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                        <defs>
                            <linearGradient id={`${gradId}-fill`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={colors.primary.main} stopOpacity={0.28} />
                                <stop offset="100%" stopColor={colors.primary.main} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid
                            vertical={false}
                            stroke={alpha(colors.text.muted, 0.08)}
                        />
                        <XAxis
                            dataKey="timestamp"
                            type="number"
                            domain={[rangeStart, rangeEnd]}
                            ticks={xTicks}
                            tick={<ChartXAxisTick />}
                            axisLine={false}
                            tickLine={false}
                            interval={0}
                        />
                        <YAxis
                            domain={yDomain}
                            tick={<ChartYAxisTick />}
                            axisLine={false}
                            tickLine={false}
                            width={34}
                        />
                        {Number.isFinite(communityAvg) ? (
                            <ReferenceLine
                                y={communityAvg}
                                stroke={COMMUNITY_AVG_COLOR}
                                strokeDasharray="5 5"
                                strokeWidth={1.5}
                            />
                        ) : null}
                        <Tooltip
                            content={<HistoryTooltip />}
                            cursor={{
                                stroke: alpha(colors.primary.main, 0.2),
                                strokeWidth: 1,
                                strokeDasharray: "4 4",
                            }}
                        />
                        <Area
                            type="linear"
                            dataKey="score"
                            stroke={colors.primary.main}
                            strokeWidth={2}
                            fill={`url(#${gradId}-fill)`}
                            dot={points.length > MIN_LINE_POINTS
                                ? false
                                : { r: 3, fill: colors.primary.main, strokeWidth: 0 }}
                            activeDot={{
                                r: 5,
                                fill: colors.primary.main,
                                stroke: alpha(colors.primary.main, 0.35),
                                strokeWidth: 2,
                            }}
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
}

ReputationHistoryChart.propTypes = {
    history: PropTypes.arrayOf(PropTypes.object),
    currentScore: PropTypes.number,
    communityAvg: PropTypes.number,
};
